import { Component, Input, OnChanges } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IValidationResults } from "@app/main/modules/foundation/foundation.interfaces";
import { DateService } from "@app/services/date.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize } from "rxjs/operators";
import { ValidationStatus, ValidationTestResult } from "../../_services/device-data-validation.service";
import { IVehicleValidationCheckRecord, IVehicleValidationCheckRecordQueryOptions, StringSearchType, VehicleValidationCheckRecordService } from "../../_services/vehicle-validation-check-record.service";
import "./device-validation-summary.component.scss";

export interface IValidationCheckRecordWithResults extends IVehicleValidationCheckRecord {
    results?: ValidationTestResult[];
}

export interface IValidationSession {
    sessionTime: Date;
    commissonId: number;
    records: IValidationCheckRecordWithResults[];
}

@UntilDestroy()
@Component({
    selector: "device-validation-summary",
    template: require('./device-validation-summary.component.html')
})

export class DeviceValidationSummaryComponent implements OnChanges {

    @Input() vehicle: IVehicle;
    @Input() serial: string;

    constructor(
        private readonly vTableService: VTableService,
        private readonly vehicleValidationCheckRecordService: VehicleValidationCheckRecordService,
        private readonly dateService: DateService) {
    }

    validationRecordColumns: IVTableColumn<IValidationSession>[];
    validationRecordsRows: IVTableRow[];

    sessions: IValidationSession[] = [];

    loading: boolean;

    ngOnInit(): void {
        this.createDataTable();
        this.loadData();
    }

    ngOnChanges(): void {
        this.loadData();
    }

    getDateOneDayAgo(): Date {
        var date = new Date();
        date.setDate(date.getDate() - 1);
        return date;
    }

    validationComplete($event: IValidationResults): void {
        
    }

    private loadData(): void {
        this.loading = true;

        var options: IVehicleValidationCheckRecordQueryOptions = {
            Serial: { Value: this.serial, Type: StringSearchType.Match }
        };

        this.vehicleValidationCheckRecordService.getVehicleValidationCheckRecords(options).pipe(untilDestroyed(this), finalize(() => this.loading = false)).subscribe(records => {
            this.sessions = [];

            records.forEach((record: IValidationCheckRecordWithResults) => {
                record.results = JSON.parse(record.ValidationCheckResultJson) as ValidationTestResult[];

                let existingSession = this.sessions.find(session => this.dateService.formatDate(session.sessionTime, { dateTime: true }) === this.dateService.formatDate(record.EventTime, { dateTime: true }));

                if (!existingSession) {
                    // Create a new session if no existing session found
                    existingSession = { sessionTime: record.EventTime, records: [], commissonId: record.CommissionId }
                    this.sessions.push(existingSession);
                }

                existingSession.records.push(record);
            })

            this.validationRecordsRows = this.vTableService.createTableRows(this.sessions, this.validationRecordColumns);
        })
    }

    private createDataTable(): void {
        this.validationRecordColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: (validationSession) => this.vTableService.createStatusCell(this.countRecordResults(validationSession.records, ValidationStatus.Fail) > 0 ? VTableRowStatus.Error : VTableRowStatus.Success)
            },
            {
                header: this.vTableService.createDateTableHeader({ sorted: true }),
                createCellFunction: (validationSession) => this.vTableService.createDateCell(validationSession.sessionTime)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceValidationSummary.SuccessfulChecks" }),
                createCellFunction: (validationSession) => this.vTableService.createTableCell(this.countRecordResults(validationSession.records, ValidationStatus.Pass))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceValidationSummary.Warnings" }),
                createCellFunction: (validationSession) => this.vTableService.createTableCell(this.countRecordResults(validationSession.records, ValidationStatus.Warning))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceValidationSummary.FailedChecks" }),
                createCellFunction: (validationSession) => this.vTableService.createTableCell(this.countRecordResults(validationSession.records, ValidationStatus.Fail))
            }
        ];
    }

    private countRecordResults(records: IValidationCheckRecordWithResults[], statusToCount: ValidationStatus): number {
        return records.reduce((count, record) => {
            return count + record.results.filter(result => result.Status === statusToCount).length;
        }, 0);
    }
}
